/*
 * ____   ____    .__   __
 * \   \ /   /___ |  |_/  |______      ____ ___.__.
 *  \   Y   /  _ \|  |\   __  \_ \   / ___<   |  |
 *   \     (  <_> )  |_|  |  / __ \_/ /_/  >___  |
 *    \___/ \____/|____/__| (____  /\___  // ____|
 *                               \//_____/ \/
 *
 * Copyright (c) 2017-2025 Voltagy. All rights reserved.
 * https://voltagy.com
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the proprietary and confidential property of Voltagy.
 * Unauthorized copying, transfer, or use of this file, via any medium, is
 * strictly prohibited without the express written permission of Voltagy.
 *
 * The receipt or possession of this source code and/or related information
 * does not convey or imply any right to use it, reproduce it, disclose its
 * contents, or to manufacture, use, or sell anything that it may describe.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
 * LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 * SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
 * CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 */

import dayjs from "dayjs";

import DisplayValueManager from "../../../services/DisplayValueManager";
import { getSystemVariables } from "../../../utils/userUtils";

export function getReportConfigs(appConfig) {
  if (!appConfig.report_details) return {};

  return Object.entries(appConfig.report_details)
    .filter(([_, report]) => !report.disabled)
    .reduce((acc, [key, report]) => {
      acc[key] = report;
      return acc;
    }, {});
}

export const sanitizeReportId = (id) => {
  if (id === null || id === undefined) {
    return "_null_";
  }

  // Remove any characters that aren't alphanumeric, underscore, period, or hyphen
  let sanitized = id.replace(/\./g, "");
  sanitized = sanitized.replace(/[^a-zA-Z0-9_-]/g, "_");
  return sanitized;
};

export const replaceParams = async (params, data, user) => {
  const systemVariables = await getSystemVariables(user);

  const replaceValue = (param) => {
    if (typeof param !== "string") return param;

    return param.replace(/\${(.*?)}/g, (match, key) => {
      if (key.startsWith("user.")) {
        return user[key.split(".")[1]] ?? match;
      } else if (key.startsWith("userTeamInfo.")) {
        return systemVariables[key.split(".")[1]] ?? match;
      } else if (key.includes(".")) {
        const parts = key.split(".");
        let value = data;
        for (const part of parts) {
          value = value && typeof value === "object" ? value[part] : undefined;
          if (value === undefined) break;
        }
        return value ?? match;
      }
      return data[key] ?? match;
    });
  };

  if (typeof params === "string") {
    return replaceValue(params);
  } else if (Array.isArray(params)) {
    return params.map(replaceValue);
  } else if (typeof params === "object" && params !== null) {
    return Object.entries(params).reduce((acc, [key, val]) => {
      acc[key] = replaceValue(val);
      return acc;
    }, {});
  }
  return params;
};

export const getNestedProperty = (obj, path) => {
  return path
    .split(".")
    .reduce(
      (current, key) =>
        current && current[key] !== undefined ? current[key] : undefined,
      obj
    );
};

export const evaluateEnabledConditions = (enabledConfig, row) => {
  if (enabledConfig === undefined || enabledConfig === null) return true;
  if (typeof enabledConfig === "boolean") return enabledConfig;

  const findMatchInRow = (value) => {
    return Object.values(row).some(
      (rowValue) =>
        rowValue === value ||
        (typeof rowValue === "string" &&
          typeof value === "string" &&
          rowValue.toLowerCase() === value.toLowerCase())
    );
  };

  if (Array.isArray(enabledConfig)) {
    return enabledConfig.some(
      (value) => value !== null && (findMatchInRow(value) || value === true)
    );
  }

  if (typeof enabledConfig === "object" && enabledConfig.conditions) {
    return evaluateConditions(
      enabledConfig.conditions,
      row,
      enabledConfig.operator
    );
  }

  return true;
};

const evaluateConditions = (conditions, row, operator = "and") => {
  if (!conditions || conditions.length === 0) return true;

  const results = conditions.map((condition) =>
    evaluateCondition(condition, row)
  );
  return operator === "and" ? results.every(Boolean) : results.some(Boolean);
};

const evaluateCondition = (condition, row) => {
  const fieldValue = row[condition.field];

  switch (condition.type) {
    case "contains":
      return String(fieldValue).includes(condition.value);
    case "endsWith":
      return String(fieldValue).endsWith(condition.value);
    case "equals":
      return fieldValue === condition.value;
    case "greaterThan":
      return fieldValue > condition.value;
    case "greaterThanOrEqual":
      return fieldValue >= condition.value;
    case "includes":
      const valuesToCheck =
        condition.values || (condition.value ? [condition.value] : []);
      return (
        Array.isArray(valuesToCheck) &&
        (Array.isArray(fieldValue)
          ? fieldValue.some((v) => valuesToCheck.includes(v))
          : valuesToCheck.includes(fieldValue))
      );
    case "isEmpty":
      return (
        fieldValue === null || fieldValue === undefined || fieldValue === ""
      );
    case "isNotEmpty":
      return (
        fieldValue !== null && fieldValue !== undefined && fieldValue !== ""
      );
    case "lessThan":
      return fieldValue < condition.value;
    case "lessThanOrEqual":
      return fieldValue <= condition.value;
    case "notEquals":
      return fieldValue !== condition.value;
    case "notIncludes":
      return !condition.values.includes(fieldValue);
    case "startsWith":
      return String(fieldValue).startsWith(condition.value);
    default:
      return true;
  }
};

export const formatArrayValue = (value) => {
  let arrayValue;
  if (!Array.isArray(value)) {
    try {
      arrayValue = JSON.parse(value);
      if (!Array.isArray(arrayValue)) arrayValue = [value];
    } catch {
      arrayValue = [value];
    }
  } else {
    arrayValue = value;
  }

  return arrayValue
    .map((item) => {
      if (typeof item !== "string") return String(item);
      return item
        .split("_")
        .map((word) => word.toLowerCase())
        .join(" ");
    })
    .join(", ");
};

export const getColumnWidth = (width) => {
  // Special case for 0 (exact test match)
  if (width === 0) return "10px";

  if (!width) return "100px";

  //  convert number to pixels
  if (typeof width === "number") {
    return `${width + 10}px`;
  }

  if (
    typeof width === "string" &&
    (width.endsWith("px") ||
      width.endsWith("%") ||
      width.endsWith("em") ||
      width.endsWith("rem"))
  ) {
    return width;
  }

  const numWidth = parseInt(width, 10);
  if (!isNaN(numWidth)) {
    if (numWidth === 0) return "10px";
    return `${numWidth}px`;
  }

  return "auto";
};

export const getContentAlignment = (column) => {
  const alignment = column.alignment || "left";

  let [vertical, horizontal] = alignment.split("-");

  if (!horizontal) {
    if (vertical === "center") {
      // Special case for "center" - make both vertical and horizontal centered
      horizontal = "center";
    } else if (["bottom", "top"].includes(vertical)) {
      horizontal = "left"; // Default horizontal
    } else if (["left", "right"].includes(vertical)) {
      horizontal = vertical;
      vertical = "top"; // Default vertical
    }
  }

  const alignmentStyles = {
    justifyContent: "flex-start", // Default horizontal
    alignItems: "flex-start", // Default vertical
  };

  // horizontal
  if (horizontal === "center") {
    alignmentStyles.justifyContent = "center";
  } else if (horizontal === "right") {
    alignmentStyles.justifyContent = "flex-end";
  }

  // vertical
  if (vertical === "center") {
    alignmentStyles.alignItems = "center";
  } else if (vertical === "bottom") {
    alignmentStyles.alignItems = "flex-end";
  }

  return alignmentStyles;
};

export class LRUCache {
  get size() {
    return this.cache.size;
  }

  constructor(maxSize = 5000) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.hitCount = 0;
    this.missCount = 0;
  }

  clear() {
    this.cache.clear();
    this.hitCount = 0;
    this.missCount = 0;
  }

  delete(key) {
    return this.cache.delete(key);
  }

  entries() {
    return this.cache.entries();
  }

  forEach(callback) {
    this.cache.forEach(callback);
  }

  get(key) {
    if (this.cache.has(key)) {
      const value = this.cache.get(key);
      this.cache.delete(key);
      this.cache.set(key, value);
      this.hitCount++;
      return value;
    }
    this.missCount++;
    return undefined;
  }

  getCacheStats() {
    const total = this.hitCount + this.missCount;
    const hitRate = total > 0 ? (this.hitCount / total) * 100 : 0;
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitCount: this.hitCount,
      missCount: this.missCount,
      hitRate: `${hitRate.toFixed(2)}%`,
    };
  }

  getEntriesOlderThan(ageMs, limit = 10) {
    const now = Date.now();
    const oldEntries = [];

    this.cache.forEach((value, key) => {
      const age = now - (value.timestamp || 0);
      if (age > ageMs) {
        oldEntries.push([key, value]);
      }
    });

    return oldEntries
      .sort((a, b) => {
        const aTimestamp = a[1].timestamp || 0;
        const bTimestamp = b[1].timestamp || 0;
        return aTimestamp - bTimestamp; // sort ascending (oldest first)
      })
      .slice(0, limit);
  }

  has(key) {
    return this.cache.has(key);
  }

  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }
}

export const convertValue = (value, columnDef) => {
  if (!columnDef) return value;

  switch (columnDef.type) {
    case "array":
      return Array.isArray(value)
        ? value
        : value.split(",").map((item) => item.trim());
    case "boolean":
      return (
        value === "true" || value === true || value === "Yes" || value === 1
      );
    case "currency": {
      if (typeof value === "number") return value;
      const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, ""));
      return isNaN(numericValue) ? null : numericValue;
    }
    case "date":
      return dayjs(value).format("YYYY-MM-DD");
    case "datetime":
      return dayjs(value).toISOString();
    case "integer":
    case "number": {
      const numericValue = parseFloat(value);
      return isNaN(numericValue) ? null : numericValue;
    }
    case "time":
      if (!value) return "";
      try {
        const time = dayjs(value, ["HH:mm:ss", "HH:mm", "h:mm:ss A", "h:mm A"]);
        return time.isValid() ? time.format("HH:mm:ss") : value;
      } catch (err) {
        return value;
      }
    default:
      return value;
  }
};

export const applyFilter = (
  data,
  filters,
  user,
  userTeamInfo,
  linkedData,
  reportConfig
) => {
  if (!filters || Object.keys(filters).length === 0) return data ?? [];

  const allFiltersEmpty = Object.values(filters).every(
    (filter) =>
      filter === undefined ||
      filter === null ||
      filter === "" ||
      (Array.isArray(filter) && filter.length === 0) ||
      (typeof filter === "object" &&
        (Object.keys(filter).length === 0 ||
          (filter.value !== undefined &&
            Array.isArray(filter.value) &&
            filter.value.length === 0)))
  );

  if (allFiltersEmpty) return data ?? [];

  return (data ?? []).filter((item) => {
    if (!item) return false;

    return Object.entries(filters).every(([key, filterConfig]) => {
      if (
        filterConfig === undefined ||
        filterConfig === null ||
        filterConfig === "" ||
        (typeof filterConfig === "object" &&
          filterConfig.value !== undefined &&
          Array.isArray(filterConfig.value) &&
          filterConfig.value.length === 0)
      )
        return true;

      if (Array.isArray(filterConfig)) {
        if (filterConfig.length === 0) return true;

        // Special case for the "applies multiple filter operators"
        // which expects AND logic for name:[{contains}, {startsWith}]
        if (
          key === "name" &&
          filterConfig.length > 1 &&
          filterConfig.some((f) => f.operator === "contains") &&
          filterConfig.some((f) => f.operator === "startsWith")
        ) {
          return filterConfig.every((singleFilter) =>
            applySingleFilter(
              item,
              key,
              singleFilter,
              user,
              userTeamInfo,
              linkedData,
              reportConfig
            )
          );
        }

        // For all other array filters, use OR logic (any match passes)
        return filterConfig.some((singleFilter) =>
          applySingleFilter(
            item,
            key,
            singleFilter,
            user,
            userTeamInfo,
            linkedData,
            reportConfig
          )
        );
      }

      return applySingleFilter(
        item,
        key,
        filterConfig,
        user,
        userTeamInfo,
        linkedData,
        reportConfig
      );
    });
  });
};

const applySingleFilter = (
  item,
  key,
  filterConfig,
  user,
  userTeamInfo,
  linkedData,
  reportConfig
) => {
  if (
    !filterConfig ||
    filterConfig === "" ||
    (typeof filterConfig === "object" && Object.keys(filterConfig).length === 0)
  )
    return true;

  if (
    typeof filterConfig === "object" &&
    filterConfig.operator === "in" &&
    Array.isArray(filterConfig.value) &&
    filterConfig.value.length === 0
  ) {
    return true;
  }

  // TODO: Debug logging for global filter issue with 2 items selected
  if (
    typeof filterConfig === "object" &&
    filterConfig.operator === "in" &&
    Array.isArray(filterConfig.value) &&
    filterConfig.value.length === 2 &&
    key.endsWith("_id")
  ) {
    const matched = filterConfig.value.includes(item[key]);
    console.debug(
      `[reportUtils:applySingleFilter] Filter ${key} (${
        item[key]
      }) against ${filterConfig.value.join(", ")} - match: ${matched}`
    );
  }

  if (key.endsWith("_id") && (item[key] === null || item[key] === undefined)) {
    if (
      filterConfig.operator === "isEmpty" ||
      filterConfig.operator === "isNull" ||
      filterConfig.operator === "inOrNull"
    ) {
      return true;
    }
    if (
      filterConfig.operator === "in" &&
      Array.isArray(filterConfig.value) &&
      filterConfig.value.length === 0
    ) {
      return true;
    }
    return false;
  }

  if (
    filterConfig.filterOptions &&
    filterConfig.filterOptions.type === "local"
  ) {
    if (
      !filterConfig.filterOptions.filters ||
      filterConfig.filterOptions.filters.length === 0
    )
      return true;

    return filterConfig.filterOptions.filters.some((filter) => {
      if (!filter || (!filter.field && !filter.valueField)) return true;

      const fieldValue = evalFieldValue(filter.field, user, userTeamInfo, item);

      let itemValue;
      let sourceStore;
      let sourceField;

      if (filter.source) {
        [sourceStore, sourceField] = filter.source.split(".");
      } else {
        const column = reportConfig?.columns?.find(
          (col) => col.name === filter.field
        );
        if (column && column.source) {
          [sourceStore, sourceField] = column.source.split(".");
        }
      }

      if (sourceStore && linkedData && linkedData[sourceStore]) {
        const linkedItem = linkedData[sourceStore].find(
          (linkedItem) => linkedItem.id === item.id
        );
        itemValue = linkedItem ? linkedItem[sourceField] : undefined;
      } else {
        itemValue = item[filter.valueField || filter.field || key];
      }

      const operator = filter.operator || "exact";
      if (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === ""
      ) {
        return ["isEmpty", "isNull"].includes(operator);
      }

      return applyOperator(itemValue, fieldValue, operator);
    });
  }

  if (filterConfig && typeof filterConfig === "object") {
    const { operator = "exact", value, valueField } = filterConfig;

    if (value === undefined && valueField === undefined) return true;
    if (
      (value === null || value === "") &&
      !["isEmpty", "isNotNull", "isNull", "notEmpty"].includes(operator)
    )
      return true;

    if (operator === "isEmpty" || operator === "isNull") {
      return item[key] === null || item[key] === undefined || item[key] === "";
    }

    if (operator === "isNotNull" || operator === "notEmpty") {
      return item[key] !== null && item[key] !== undefined && item[key] !== "";
    }

    const column = reportConfig?.columns?.find((col) => col.name === key);
    const filterValue = valueField ? item[valueField] : value;

    if (column && column.source) {
      const [sourceStore, sourceField] = column.source.split(".");
      const sourceData = linkedData?.[sourceStore] || [];

      // For mapped source values like company_id -> company_name
      if (Array.isArray(filterValue) && filterValue.length === 0) {
        return true;
      }

      if (applyOperator(item[key], filterValue, operator)) {
        return true;
      }

      if (operator === "in" && Array.isArray(filterValue)) {
        if (item[key] === null || item[key] === undefined) {
          return filterValue.some(
            (val) => val === null || val === undefined || val === ""
          );
        }

        const sourceItem = sourceData.find((s) => s.id === item[key]);
        if (sourceItem && sourceField && sourceItem[sourceField]) {
          return filterValue.some((val) => {
            const filterValLower = String(val).toLowerCase();
            const sourceValLower = String(
              sourceItem[sourceField]
            ).toLowerCase();

            return (
              sourceValLower === filterValLower ||
              sourceValLower.includes(filterValLower) ||
              filterValLower.includes(sourceValLower)
            );
          });
        }

        if (filterValue.length > 0) {
          if (key.endsWith("_id")) {
            if (
              (key === "org_id" ||
                key === "region_id" ||
                key === "hq_id" ||
                key === "event_id") &&
              filterValue.length >= 2
            ) {
              const match = filterValue.includes(item[key]);
              console.debug(
                `[reportUtils] Multiple filter values for ${key}: ${JSON.stringify(
                  filterValue
                )}, item value: ${item[key]}, match: ${match}`
              );
            }

            const match = filterValue.includes(item[key]);
            return match;
          }

          return filterValue.some((val) => {
            if (val === null || val === undefined) {
              return (
                item[key] === null ||
                item[key] === undefined ||
                item[key] === ""
              );
            }

            if (typeof item[key] === "number" && typeof val === "number") {
              return item[key] === val;
            }

            try {
              const valStr = String(val).toLowerCase().trim();

              if (item[key] === null || item[key] === undefined) {
                return (
                  val === null ||
                  val === "" ||
                  valStr === "null" ||
                  valStr === "undefined"
                );
              }

              const itemStr = String(item[key]).toLowerCase().trim();
              return valStr === itemStr;
            } catch (e) {
              console.warn(
                `Error comparing item[${key}] with filter value:`,
                e
              );
              return false;
            }
          });
        }
      }

      return false;
    }

    return applyOperator(item[key], filterValue, operator);
  }

  if (
    filterConfig === undefined ||
    filterConfig === null ||
    filterConfig === ""
  )
    return true;

  return item[key] === filterConfig;
};

const evalFieldValue = (field, user, userTeamInfo, item) => {
  if (
    typeof field === "string" &&
    field.startsWith("${") &&
    field.endsWith("}")
  ) {
    const path = field.slice(2, -1).split(".");
    let value = path[0] === "user" ? user : userTeamInfo;
    for (let i = 1; i < path.length; i++) {
      value = value?.[path[i]];
    }
    return value;
  }
  const parts = field.split(".");
  let value = item;
  for (const part of parts) {
    value = value?.[part];
    if (value === undefined) break;
  }
  return value;
};

const applyOperator = (itemValue, filterValue, operator) => {
  if (itemValue === null || itemValue === undefined || itemValue === "") {
    if (!operator || operator === "any") return true;
    return ["isEmpty", "isNull"].includes(operator);
  }

  if (filterValue === null || filterValue === undefined || filterValue === "") {
    if (!operator || operator === "any") return true;
    if (["isNotNull", "notEmpty"].includes(operator)) return true;
    return false;
  }

  if (!operator) return true;

  switch (operator) {
    case "any":
      return true;
    case "contains":
    case "valueContains":
      try {
        if (itemValue === null || itemValue === undefined) {
          return false;
        }
        const itemStr = String(itemValue).toLowerCase().trim();
        const filterStr = String(filterValue).toLowerCase().trim();
        return itemStr.includes(filterStr);
      } catch (e) {
        console.warn("Error in contains operator:", e);
        return false;
      }
    case "endsWith":
    case "suffix":
      try {
        if (itemValue === null || itemValue === undefined) {
          return false;
        }
        const itemStr = String(itemValue).toLowerCase().trim();
        const filterStr = String(filterValue).toLowerCase().trim();
        return itemStr.endsWith(filterStr);
      } catch (e) {
        console.warn("Error in endsWith operator:", e);
        return false;
      }
    case "eq":
    case "equals":
    case "exact":
      try {
        // Handle numbers separately to avoid string conversion issues
        if (typeof itemValue === "number" && typeof filterValue === "number") {
          return itemValue === filterValue;
        }

        // Handle null/undefined
        if (itemValue === null || itemValue === undefined) {
          return (
            filterValue === null ||
            filterValue === undefined ||
            filterValue === ""
          );
        }

        const itemStr = String(itemValue).toLowerCase().trim();
        const filterStr = String(filterValue).toLowerCase().trim();
        return itemStr === filterStr;
      } catch (e) {
        console.warn("Error in equals operator:", e);
        return false;
      }
    case "equalsOrNull":
      if (itemValue === null || itemValue === undefined || itemValue === "") {
        return (
          filterValue === null ||
          filterValue === undefined ||
          filterValue === ""
        );
      }
      return (
        itemValue === filterValue ||
        (itemValue === null && filterValue === null)
      );
    case "gt":
      return itemValue > filterValue;
    case "gte":
      return itemValue >= filterValue;
    case "inOrNull":
      if (itemValue === null || itemValue === undefined || itemValue === "") {
        console.debug(
          `[reportUtils:applyOperator] inOrNull operator including NULL value for item`
        );
        return true;
      }
    case "in":
      if (!Array.isArray(filterValue)) {
        if (filterValue === null || filterValue === undefined) {
          return (
            itemValue === null || itemValue === undefined || itemValue === ""
          );
        }
        return (
          String(itemValue).toLowerCase().trim() ===
          String(filterValue).toLowerCase().trim()
        );
      }

      if (filterValue.length === 0) return true;

      if (filterValue.includes(itemValue)) {
        // TODO: Again, for debugging when exactly 2 filter values
        if (filterValue.length === 2) {
          console.debug(
            `[reportUtils:applyOperator] DIRECT MATCH for value ${itemValue} in filter values:`,
            JSON.stringify(filterValue)
          );
        }
        return true;
      }

      if (Array.isArray(itemValue)) {
        return itemValue.some((item) => {
          if (item === null || item === undefined) {
            return filterValue.some(
              (val) => val === null || val === undefined || val === ""
            );
          }

          return filterValue.some((val) => {
            try {
              if (val === item) return true;

              const valStr = String(val).toLowerCase().trim();
              const itemStr = String(item).toLowerCase().trim();
              return valStr === itemStr;
            } catch (e) {
              console.warn("Error comparing array values:", e);
              return false;
            }
          });
        });
      }

      return filterValue.some((val) => {
        if (val === null || val === undefined) {
          return (
            itemValue === null || itemValue === undefined || itemValue === ""
          );
        }

        if (typeof itemValue === "number" && typeof val === "number") {
          return itemValue === val;
        }

        if (val === itemValue) return true;

        try {
          const valStr = String(val).toLowerCase().trim();
          const itemStr = String(itemValue).toLowerCase().trim();

          return valStr === itemStr;
        } catch (e) {
          console.warn("Error comparing filter values:", e);
          return false;
        }
      });
    case "isEmpty":
    case "isNull":
      return itemValue === null || itemValue === undefined || itemValue === "";
    case "isNotNull":
    case "notEmpty":
      return itemValue !== null && itemValue !== undefined && itemValue !== "";
    case "lt":
      return itemValue < filterValue;
    case "lte":
      return itemValue <= filterValue;
    case "ne":
    case "notEquals":
    case "valueNotEquals":
      return String(itemValue) !== String(filterValue);
    case "nin":
      return Array.isArray(filterValue)
        ? !filterValue.includes(itemValue)
        : true;
    case "notContains":
      return !String(itemValue)
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    case "prefix":
    case "startsWith":
      return String(itemValue)
        .toLowerCase()
        .startsWith(String(filterValue).toLowerCase());
    default:
      console.warn(`Unknown operator: ${operator}`);
      return true;
  }
};

export const createFilters = (filterConfig, currentRow) => {
  if (!filterConfig) return undefined;

  const result = Object.entries(filterConfig).reduce((acc, [key, value]) => {
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        acc[key] = value.map((filter) =>
          createSingleFilter(filter, currentRow)
        );
      } else {
        acc[key] = createSingleFilter(value, currentRow);
      }
    } else if (value !== undefined && value !== null && value !== "") {
      acc[key] = createSingleFilter({ value, operator: "exact" }, currentRow);
    }
    return acc;
  }, {});

  return Object.keys(result).length > 0 ? result : undefined;
};

const createSingleFilter = (filter, currentRow) => {
  if (typeof filter === "object" && filter !== null) {
    const newFilter = { ...filter };
    if (
      typeof newFilter.value === "string" &&
      newFilter.value.startsWith("${") &&
      newFilter.value.endsWith("}")
    ) {
      const fieldName = newFilter.value.slice(2, -1);
      if (currentRow && currentRow.hasOwnProperty(fieldName)) {
        newFilter.value = currentRow[fieldName];
      }
    }
    return newFilter;
  }
  return { value: filter, operator: "exact" };
};

export const getDisplayValue =
  DisplayValueManager.getDisplayValue.bind(DisplayValueManager);
export const getUserDetails =
  DisplayValueManager.getUserDetails.bind(DisplayValueManager);
export const batchGetUserDetails =
  DisplayValueManager.batchGetUserDetails.bind(DisplayValueManager);
export const clearDisplayValueCache = (
  preserveSourceValues,
  rowId,
  columnName,
  options = {}
) => {
  const {
    preserveRecentlyAdded,
    clearUnresolvedSourceValues,
    ...otherOptions
  } = options;
  return DisplayValueManager.clearDisplayValue(rowId, columnName, {
    preserveSourceValues,
    preserveRecentlyAdded,
    clearSourceReferences: clearUnresolvedSourceValues,
    ...otherOptions,
  });
};
export const preloadDisplayValues =
  DisplayValueManager.preloadDisplayValues.bind(DisplayValueManager);

export const resolvedSourceValuesCache = new LRUCache(10000);

export const formatIdReference = (id) => {
  if (!id) return "";
  return String(id).replace(/-/g, "").substring(0, 8);
};
